// Rescale one drawing's takeoffs when its scale changes.
//
// Rooms and drawn conditions are stored in WORLD FEET; the drawing underlay is
// sized from the sheet's scale. Changing the scale resizes the underlay about
// the sheet's origin (ImageX, ImageY), so unless the takeoffs on that sheet move
// with it they no longer sit over what they measured and every stored length /
// area is wrong. This builds the change: every point of the sheet's rooms and
// conditions is scaled by k = newScale / oldScale about the origin, lengths by
// k and areas by k². Real-world sizes — a door's width, a room's height — are
// properties of the thing, not of the paper, and are left alone.
//
// Edge Run overrides and auto-wall rows are keyed by COORDINATES
// (takeoffGeometry.runKeyFor), so a uniform scale would orphan every manual
// override and every hand-assigned wall type. They are carried over by matching
// each edge / wall segment before and after the scale (same pair, same
// perimeter edges, same order) and handing the proc an old-key -> new-key list.
//
// Pure: nothing here reads or writes the database. The proc
// (sp_RescaleSheetTakeoffs, db/38) applies the result atomically with the new
// scale.

import { detectEdges, computeAutoWallSegments } from "./takeoffGeometry";

export const RESCALE_MIN_CHANGE = 0.001; // below 0.1 % is a re-save, not a rescale

/** k = newScale / oldScale, or null when either is not a positive number. */
export function scaleFactor(prevScale, nextScale) {
  const a = +prevScale, b = +nextScale;
  return a > 0 && b > 0 ? b / a : null;
}

export const isMaterialChange = (k) => k != null && Math.abs(k - 1) >= RESCALE_MIN_CHANGE;

const sc = (v, k, o) => o + (v - o) * k;

export function scalePoint(p, k, ox, oy) {
  return { ...p, x: sc(+p.x, k, ox), y: sc(+p.y, k, oy) };
}

const parse = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return null; }
};

/** A room's PolygonJson ({ points, areaFt2, perimeterFt }) scaled; null in -> null out. */
export function rescalePolygonJson(json, k, ox, oy) {
  const g = parse(json);
  if (!g || !Array.isArray(g.points)) return json ?? null;
  const out = { ...g, points: g.points.map(p => scalePoint(p, k, ox, oy)) };
  if (g.areaFt2 != null) out.areaFt2 = g.areaFt2 * k * k;
  if (g.perimeterFt != null) out.perimeterFt = g.perimeterFt * k;
  return JSON.stringify(out);
}

/** The columns the proc writes for one room. */
export function rescaleRoom(room, k, ox, oy) {
  const has = (v) => v != null && v !== "" && Number.isFinite(+v);
  return {
    room_id: room.RoomID,
    canvas_x: has(room.CanvasX) ? sc(+room.CanvasX, k, ox) : null,
    canvas_y: has(room.CanvasY) ? sc(+room.CanvasY, k, oy) : null,
    length_ft: has(room.LengthFt) ? +room.LengthFt * k : null,
    width_ft: has(room.WidthFt) ? +room.WidthFt * k : null,
    polygon_json: room.PolygonJson ? rescalePolygonJson(room.PolygonJson, k, ox, oy) : null,
  };
}

/** The room object as it will look after the rescale (for re-detecting walls). */
export function rescaledRoomObject(room, k, ox, oy) {
  const r = rescaleRoom(room, k, ox, oy);
  return {
    ...room,
    CanvasX: r.canvas_x ?? room.CanvasX, CanvasY: r.canvas_y ?? room.CanvasY,
    LengthFt: r.length_ft ?? room.LengthFt, WidthFt: r.width_ft ?? room.WidthFt,
    PolygonJson: r.polygon_json ?? room.PolygonJson,
  };
}

/**
 * A drawn condition's GeometryJson scaled. Handles a traced run / area / back-out
 * ({ points, lengthFt, areaFt2, perimeterFt }) and a placed symbol
 * ({ x, y, angleDeg, wallNormalDeg, widthFt, flip }) — a symbol's POSITION scales,
 * its real width does not. A stored tile-grid result is dropped: it is derived from
 * the polygon and the "visible tile-count sync" in RoomTakeoff recomputes it.
 */
export function rescaleConditionGeometry(json, k, ox, oy) {
  const g = parse(json);
  if (!g) return json ?? null;
  const out = { ...g };
  if (Array.isArray(g.points)) {
    out.points = g.points.map(p => scalePoint(p, k, ox, oy));
    if (g.lengthFt != null) out.lengthFt = g.lengthFt * k;
    if (g.areaFt2 != null) out.areaFt2 = g.areaFt2 * k * k;
    if (g.perimeterFt != null) out.perimeterFt = g.perimeterFt * k;
    delete out.grid;
  }
  if (typeof g.x === "number" && typeof g.y === "number") {
    out.x = sc(g.x, k, ox);
    out.y = sc(g.y, k, oy);
  }
  return JSON.stringify(out);
}

// ── key carry-over ───────────────────────────────────────────────────────────

// Pair every edge before the scale with its counterpart after it: same two rooms,
// same orientation, same perimeter edges, and the n-th such edge in generation order.
function edgeId(e, counts) {
  const base = `${e.a.RoomID}:${e.b.RoomID}:${e.orientation}:${e.edgeAIndex}:${e.edgeBIndex}`;
  const n = counts.get(base) || 0;
  counts.set(base, n + 1);
  return `${base}#${n}`;
}

/** Edge Run key changes: [{ room_a, room_b, old_run_key, new_run_key }] for edges whose key moves. */
export function runKeyChanges(oldEdges, newEdges) {
  const newById = new Map();
  const nc = new Map();
  for (const e of newEdges) newById.set(edgeId(e, nc), e);
  const oc = new Map();
  const out = [];
  for (const e of oldEdges) {
    const n = newById.get(edgeId(e, oc));
    if (!n || !e.runKey || !n.runKey || e.runKey === n.runKey) continue;
    out.push({ room_a: Math.min(e.a.RoomID, e.b.RoomID), room_b: Math.max(e.a.RoomID, e.b.RoomID), old_run_key: e.runKey, new_run_key: n.runKey });
  }
  return out;
}

const segGroup = (key) => {
  const p = String(key).split(":");
  return p[0] === "ext" ? `ext:${p[1]}` : p.slice(0, 3).join(":"); // shared:lo:hi / cavity:a:b / ext:roomId
};

/** Auto-wall key changes: [{ old_key, new_key }] pairing the n-th segment of each wall group before and after. */
export function autoWallKeyChanges(oldSegs, newSegs) {
  const group = (segs) => { const m = new Map(); for (const s of segs) { const g = segGroup(s.key); (m.get(g) || m.set(g, []).get(g)).push(s.key); } return m; };
  const og = group(oldSegs), ng = group(newSegs);
  const out = [];
  for (const [g, olds] of og) {
    const news = ng.get(g);
    if (!news || news.length !== olds.length) continue; // topology changed — the sync recreates these
    olds.forEach((ok, i) => { if (ok !== news[i]) out.push({ old_key: ok, new_key: news[i] }); });
  }
  return out;
}

/**
 * The whole change for one sheet.
 *   factor, origin — k and the sheet's (ImageX, ImageY)
 *   rooms          — this sheet's rooms; conditions — this sheet's drawn rows (incl. back-outs)
 *   edgeOverrides  — Map/obj keyed by edgeOverrideKey (only its keys' presence matters here)
 *   tunables, rulesByPair, wallSystemByRoom — as RoomTakeoff passes to the wall engine
 */
export function buildSheetRescale({ factor: k, origin, rooms, conditions, edgeOverrides = {}, tunables, rulesByPair = null, wallSystemByRoom = null }) {
  const ox = +origin?.x || 0, oy = +origin?.y || 0;
  const newRooms = rooms.map(r => rescaledRoomObject(r, k, ox, oy));

  const oldEdges = detectEdges(rooms, edgeOverrides, tunables);
  const newEdges = detectEdges(newRooms, edgeOverrides, tunables);
  const runKeys = runKeyChanges(oldEdges, newEdges);

  const opts = { wallSystemByRoom };
  const oldSegs = computeAutoWallSegments(rooms, oldEdges, rulesByPair, tunables, opts);
  const newSegs = computeAutoWallSegments(newRooms, newEdges, rulesByPair, tunables, opts);
  const autoWallKeys = autoWallKeyChanges(oldSegs, newSegs);

  return {
    rooms: rooms.map(r => rescaleRoom(r, k, ox, oy)),
    conditions: conditions
      .filter(c => c.GeometryJson)
      .map(c => ({
        id: c.TakeoffConditionDrawnID,
        geometry_json: rescaleConditionGeometry(c.GeometryJson, k, ox, oy),
        // an auto-wall row's QuantityOverride mirrors its LENGTH (the sync rewrites it); a count
        // symbol's override of 1 and any hand-typed quantity are not lengths of the drawing
        ...(c.AutoWallKey && c.QuantityOverride != null ? { quantity_override: +c.QuantityOverride * k } : {}),
      })),
    run_keys: runKeys,
    auto_wall_keys: autoWallKeys,
  };
}

